import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Download, X, Archive, File, Folder, Loader2 } from 'lucide-react';
import { eventBus } from '../core/EventBus';
import { fileDownloadService } from '../core/FileDownloadService';
import { t } from './FileManager/i18n';

interface ClipboardFileInfo {
  index: number;
  name: string;
  path: string;
  size: number;
  fileCount: number;
  isDir: boolean;
}

interface DownloadProgress {
  fileIndex: number;
  fileName: string;
  totalSize: number;
  downloadedBytes: number;
  status: 'pending' | 'preparing' | 'downloading' | 'completed' | 'failed';
  // ZIP 场景由 host 上报的原始字节进度（与 totalSize/downloadedBytes 单位不同，前者是 zip 流字节）
  readBytes?: number;
  originalTotal?: number;
  bytesPerSecond?: number;
}

interface ToastState {
  files: ClipboardFileInfo[];
  totalSize: number;
  fileCount: number;
}

const AUTO_DISMISS_MS = 6000;

const formatSize = (bytes: number): string => {
  if (bytes < 0) return t('clipboardPendingCalc');
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const buildFileName = (files: ClipboardFileInfo[]): string => {
  if (files.length === 1 && !files[0].isDir) return files[0].name;
  if (files.length === 1 && files[0].isDir) return `${files[0].name}.zip`;
  return 'clipboard_files.zip';
};

interface Props {
  /** 父组件控制的强制显示（如 QuickActions 主动查询命中后） */
  forceShow?: boolean;
  onClose?: () => void;
}

const ClipboardFilesNotification: React.FC<Props> = ({ forceShow, onClose }) => {
  const [toast, setToast] = useState<ToastState | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const [animatingOut, setAnimatingOut] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [stackBottom, setStackBottom] = useState(16); // 与上传面板垂直堆叠时的动态 bottom
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 镜像 downloading 给 eventBus 回调用（避免闭包陷阱）
  const downloadingRef = useRef(false);
  useEffect(() => { downloadingRef.current = downloading; }, [downloading]);

  // 与上传面板垂直堆叠：测其实际高度动态算自身 bottom（折叠/展开/关闭三态自适应）
  useEffect(() => {
    const GAP = 8, BASE = 16;
    let ro: ResizeObserver | null = null;
    const apply = (el: Element | null) => {
      if (el) {
        if (!ro) {
          ro = new ResizeObserver(() => {
            const h = (el as HTMLElement).offsetHeight;
            setStackBottom(Math.min(BASE + h + GAP, window.innerHeight * 0.75));
          });
          ro.observe(el);
        }
      } else {
        if (ro) { ro.disconnect(); ro = null; }
        setStackBottom(BASE);
      }
    };
    apply(document.querySelector('[data-upload-manager]'));
    const mo = new MutationObserver(() => {
      const el = document.querySelector('[data-upload-manager]');
      if (el && !ro) apply(el);
      else if (!el && ro) apply(null);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { mo.disconnect(); if (ro) ro.disconnect(); };
  }, []);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
  }, []);

  const scheduleAutoDismiss = useCallback(() => {
    clearDismissTimer();
    dismissTimer.current = setTimeout(() => {
      setAnimatingOut(true);
      setTimeout(() => {
        setToast(null);
        setAnimatingOut(false);
        onClose?.();
      }, 300);
    }, AUTO_DISMISS_MS);
  }, [clearDismissTimer, onClose]);

  // 监听 filesAvailable（来自 InputService 拉模式查询 / QuickActions 手动拉取）
  useEffect(() => {
    const unsub = eventBus.on('clipboard:filesAvailable', (data: {
      files: unknown[];
      totalSize: number;
      fileCount: number;
    }) => {
      const files = data.files as ClipboardFileInfo[];
      if (!files || files.length === 0) return;
      console.log('[ClipboardToast] filesAvailable:', files.length, 'files,', data.totalSize, 'bytes');
      // 下载进行中：丢弃新文件，弹提示让用户稍后再试，不覆盖当前 toast 状态
      if (downloadingRef.current) {
        console.log('[ClipboardToast] download in progress, ignoring new files');
        eventBus.emit('ui:toast', {
          message: '当前文件正在下载，请稍后再试',
          type: 'warning',
        });
        return;
      }
      setToast({
        files,
        totalSize: data.totalSize,
        fileCount: data.fileCount,
      });
      setAnimatingOut(false);
      scheduleAutoDismiss();
    });
    return unsub;
  }, [scheduleAutoDismiss]);

  // 监听下载进度
  useEffect(() => {
    const unsub = eventBus.on('clipboard:downloadProgress', (prog: DownloadProgress) => {
      if (prog.status === 'preparing' || prog.status === 'downloading') {
        clearDismissTimer();
        setDownloading(true);
        setProgress(prog);
      } else if (prog.status === 'completed') {
        setProgress(null);
        setDownloading(false);
        // 下载完成后立即关闭
        setAnimatingOut(true);
        setTimeout(() => {
          setToast(null);
          setAnimatingOut(false);
          onClose?.();
        }, 300);
      } else if (prog.status === 'failed') {
        setProgress({ ...prog });
        setDownloading(false);
        scheduleAutoDismiss();
      }
    });
    return unsub;
  }, [clearDismissTimer, scheduleAutoDismiss, onClose]);

  // forceShow 由父组件外部强制（拉取按钮命中后等场景）
  useEffect(() => {
    if (forceShow && !toast) {
      // 父组件应通过 eventBus emit filesAvailable 驱动显示；这里只兜底
      setAnimatingOut(false);
    }
  }, [forceShow, toast]);

  const handleDownload = useCallback(() => {
    if (!toast || downloading) return;
    clearDismissTimer();
    setDownloading(true);
    const fileName = buildFileName(toast.files);
    fileDownloadService
      .downloadWithFiles(
        toast.files.map(f => ({ name: f.name, path: f.path, size: f.size, isDir: f.isDir })),
        fileName,
      )
      .catch(err => {
        console.error('[ClipboardToast] download failed:', err);
        eventBus.emit('ui:toast', {
          message: t('clipboardDownloadFailed', { message: String(err) }),
          type: 'error',
        });
        setDownloading(false);
        setProgress(null);
        scheduleAutoDismiss();
      });
  }, [toast, downloading, clearDismissTimer, scheduleAutoDismiss]);

  // 真正关闭 Toast（不主动 cancel 下载——下载中关闭走 handleConfirmCancel）
  const closeToast = useCallback(() => {
    clearDismissTimer();
    setAnimatingOut(true);
    setTimeout(() => {
      setToast(null);
      setAnimatingOut(false);
      setDownloading(false);
      setProgress(null);
      setShowCancelConfirm(false);
      onClose?.();
    }, 300);
  }, [clearDismissTimer, onClose]);

  // 顶部 X / "取消下载" 按钮入口
  const handleClose = useCallback(() => {
    if (downloadingRef.current) {
      // 下载中：弹二次确认，不立即取消
      clearDismissTimer();
      setShowCancelConfirm(true);
      return;
    }
    closeToast();
  }, [clearDismissTimer, closeToast]);

  // 二次确认：确认取消下载
  const handleConfirmCancel = useCallback(() => {
    fileDownloadService.cancelDownload();
    eventBus.emit('ui:toast', { message: '已取消下载', type: 'info' });
    closeToast();
  }, [closeToast]);

  // 二次确认：继续下载
  const handleDismissConfirm = useCallback(() => {
    setShowCancelConfirm(false);
  }, []);

  if (!toast) return null;

  const isMulti = toast.files.length > 1;
  const firstIsDir = toast.files[0]?.isDir;
  // 优先用 host 上报的原始字节进度（ZIP 场景百分比精确）；fallback 到 zip 字节估算（不准但兜底）
  const useReadBytes = progress !== null && (progress.originalTotal ?? 0) > 0;
  const progressCurrent = useReadBytes && progress
    ? Math.min(progress.readBytes ?? 0, progress.originalTotal ?? 0)
    : (progress?.downloadedBytes ?? 0);
  const progressTotal = useReadBytes && progress
    ? (progress.originalTotal ?? 0)
    : (progress?.totalSize ?? 0);
  const hasValidTotal = progressTotal > 0 && progressCurrent <= progressTotal;
  const progressPct = hasValidTotal ? Math.min(100, (progressCurrent / progressTotal) * 100) : 0;
  const speedBps = progress?.bytesPerSecond ?? 0;
  const speedText = speedBps > 0 ? `${formatSize(speedBps)}/s` : '';

  return (
    <div
      onMouseEnter={clearDismissTimer}
      onMouseLeave={() => { if (!downloading) scheduleAutoDismiss(); }}
      className={`fixed right-4 z-[110] w-[340px] overflow-hidden
        transition-all duration-300 ease-out
        ${animatingOut ? 'opacity-0 translate-y-4 scale-95' : 'opacity-100 translate-y-0 scale-100'}`}
      style={{
        bottom: stackBottom,
        background: 'rgba(0, 0, 0, 0.92)',
        backdropFilter: 'blur(24px) saturate(180%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '14px',
        boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.6)',
      }}
    >
      {/* 顶部：文件信息 + 关闭按钮 */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-3">
        <div className="flex-shrink-0 p-2 rounded-lg bg-white/8">
          {isMulti ? (
            <Archive size={18} className="text-white/80" />
          ) : firstIsDir ? (
            <Folder size={18} className="text-white/80" />
          ) : (
            <File size={18} className="text-white/80" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-sm text-white/90 break-all line-clamp-2">
            {isMulti
              ? `${toast.files[0]?.name || t('clipboardFileFallback')}${t('clipboardMoreItems', { count: toast.files.length })}`
              : (toast.files[0]?.name || t('clipboardFileFallback'))}
          </div>
          <div className="text-xs text-white/50 mt-0.5 font-mono">
            {downloading && progress ? (
              progress.status === 'preparing' ? (
                t('clipboardPreparing')
              ) : hasValidTotal ? (
                // 三段式：已下载 / 总计 · 速度 · 百分比
                <>
                  {formatSize(progressCurrent)} / {formatSize(progressTotal)}
                  {speedText && <> · <span className="text-emerald-300">{speedText}</span></>}
                  {' · '}
                  <span className="text-blue-300">{progressPct.toFixed(0)}%</span>
                </>
              ) : (
                // 兜底：未知总大小
                <>
                  {t('clipboardReceived', { size: formatSize(progress.downloadedBytes ?? 0) })}
                  {speedText && <> · <span className="text-emerald-300">{speedText}</span></>}
                </>
              )
            ) : (
              toast.totalSize > 0 ? formatSize(toast.totalSize) : t('clipboardPendingCalc')
            )}
          </div>
        </div>

        {downloading ? (
          <button
            onClick={handleClose}
            className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500/15 hover:bg-red-500/25 border border-red-400/30 text-red-300 hover:text-red-200 transition-all self-start"
            title={t('clipboardCancel')}
          >
            <X size={12} />
            <span className="text-xs font-medium">取消下载</span>
          </button>
        ) : (
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-all self-start"
            title={t('clipboardCancel')}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* 下方：下载按钮 / loading */}
      <div className="px-4 pb-3">
        {!downloading ? (
          <button
            onClick={handleDownload}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 transition-all"
            title={t('clipboardDownloadItem')}
          >
            <Download size={14} className="text-emerald-300" />
            <span className="text-xs text-emerald-200 font-medium">{t('clipboardDownloadItem')}</span>
          </button>
        ) : (
          <div className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/5">
            <Loader2 size={14} className="animate-spin text-white/60" />
            <span className="text-xs text-white/60">{t('clipboardPreparing')}</span>
          </div>
        )}
      </div>

      {/* 进度条 */}
      {downloading && (
        <div className="h-1 bg-white/5 mx-4 mb-3 rounded-full overflow-hidden">
          <div
            className={`h-full bg-emerald-400/80 rounded-full transition-all duration-300${hasValidTotal ? '' : ' animate-pulse'}`}
            style={{ width: `${hasValidTotal ? progressPct : 100}%` }}
          />
        </div>
      )}

      {/* 二次确认浮层：覆盖在 Toast 主体上 */}
      {showCancelConfirm && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4"
          style={{
            background: 'rgba(0, 0, 0, 0.94)',
            backdropFilter: 'blur(8px)',
            borderRadius: '14px',
          }}
        >
          <div className="text-sm text-white/90 font-medium">确认取消下载？</div>
          <div className="text-xs text-white/50">已下载内容将被丢弃</div>
          <div className="flex items-center gap-2 mt-1">
            <button
              onClick={handleConfirmCancel}
              className="px-4 py-1.5 rounded-lg bg-red-500/25 hover:bg-red-500/40 border border-red-400/40 text-red-200 text-xs font-medium transition-all"
            >
              确认取消
            </button>
            <button
              onClick={handleDismissConfirm}
              className="px-4 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/15 text-white/80 text-xs font-medium transition-all"
            >
              继续下载
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ClipboardFilesNotification);
